import React, { CSSProperties, ReactNode } from 'react';

import {
  Button,
  Checkbox,
  CheckboxGroup,
  Col,
  DatePicker,
  DateRangePicker,
  FormItem,
  Input,
  InputGroup,
  Password,
  Radio,
  RadioGroup,
  Row,
  Select,
  SelectOption,
  TextArea,
  TimePicker,
} from '@components/atoms';
import { ICheckboxGroupParams } from '@components/atoms/DataEntry/Checkbox';
import { TDatePickerParams, TDateRangePickerParams } from '@components/atoms/DataEntry/DatePicker';
import { TInputParams, TPasswordParams, TTextAreaParams } from '@components/atoms/DataEntry/Input';
import { InputNumber, TInputNumberParams } from '@components/atoms/DataEntry/InputNumber';
import { IRadioGroupParams } from '@components/atoms/DataEntry/Radio';
import { TSelect } from '@components/atoms/DataEntry/Select';
import { ITimePickerParams } from '@components/atoms/DataEntry/TimePicker';
import { IButtonParams } from '@components/atoms/General/Button';
import FormValidErrorMessage from '@components/molecules/FormValidErrorMessage';
import IconText from '@components/molecules/IconText';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { useCheckFormError } from '@hooks/useCheckFormError';
import { getNestedObjects, setNestedObjects } from '@utils/nestedObjects';
import { tuple } from 'antd/lib/_util/type';
import _ from 'lodash';

const tInputFieldType = tuple(
  'select',
  'radio',
  'number',
  'datePicker',
  'timePicker',
  'rangePicker',
  'checkbox',
  'checkboxWithCustomRender',
  'customRender',
  'button',
  'description',
  'checkboxWithAllCheck',
  'checkboxWithoutAllCheck',
  'password',
  'text',
  'textArea',
);

type TInputFieldType = typeof tInputFieldType[number] | undefined;

type TComponentType = {
  text?: string;
  dataIndex?: string | number | (string | number)[];
  options?: any[];
  form?: any;
  isDivider?: boolean;
  colSpan?: number | string;
  style?: CSSProperties;
  disabled?: boolean;
  disabledDate?: (e: any) => boolean;
  onChange?: (e?: any) => void;
  customDivider?: () => ReactNode;
  customRender?: () => ReactNode;
  function?: () => void; // TODO: 전체 함수 대상으로 적용되는건지 확인해야함.
  formData?: Record<string, any>;
  onRealtimeChange?: (e?: any) => void;
  onBlur?: (e?: any) => void;
  gone?: boolean;
} & TCrowDividerParams;

type TSelectType = Omit<TSelect, 'type'> & { type: 'select' };
type TRadioGroupType = Omit<IRadioGroupParams, 'type'> & { type: 'radio' };
type TInputNumberType = Omit<TInputNumberParams, 'type'> & { type: 'number' };
type TDatePickerType = Omit<TDatePickerParams, 'type'> & { type: 'datePicker' };
type TTimePickerType = Omit<ITimePickerParams, 'type'> & { type: 'timePicker' };
type TDateRangePickerType = Omit<TDateRangePickerParams, 'type'> & { type: 'rangePicker' };
type TCheckboxGroupType = Omit<ICheckboxGroupParams, 'type'> & {
  type: 'checkbox' | 'checkboxWithAllCheck' | 'checkboxWithCustomRender' | 'checkboxWithoutAllCheck';
};
type TCustomRenderType = { type: 'customRender' };
type TButtonType = Omit<IButtonParams, 'type'> & { type: 'button' };
type TDescriptionType = { type: 'description' };
type TPasswordType = Omit<TPasswordParams, 'type'> & { type: 'password' };
type TInputType = Omit<TInputParams, 'type'> & { type: 'text' };
type TTextAreaType = Omit<TTextAreaParams, 'type'> & { type: 'textArea' };

type TComponentArray = TComponentType &
  (
    | TSelectType
    | TRadioGroupType
    | TInputNumberType
    | TDatePickerType
    | TTimePickerType
    | TDateRangePickerType
    | TCheckboxGroupType
    | TCustomRenderType
    | TButtonType
    | TDescriptionType
    | TPasswordType
    | TInputType
    | TTextAreaType
  );

type TInputField = {
  componentArray: TComponentArray[];
  label?: ReactNode;
  labelCol?: number;
  description?: ReactNode;
  wrapperCol?: number;
  style?: CSSProperties;
  required?: boolean;
  topDescription?: () => any;
  validation?: (
    name: string | number | (string | number)[],
    value: any,
    formData: any,
  ) => Record<string, any>;
  formFieldsError?: Record<string, any>;
  borderTop?: boolean;
  borderBottom?: boolean;
};

type TGetComponent = {
  componentType: TInputFieldType;
  idx: number;
};

type TCrowDividerParams = {
  dividerHeight?: string;
};

const CRowDivider = styled(Row)<TCrowDividerParams>`
  border-right: 1px solid rgb(0, 0, 0, 0.2);
  height: ${({ dividerHeight }) => dividerHeight || '28px'};
  margin-left: 9.5px;
  margin-right: 9.5px !important;
  margin-top: 5px;
`;
type TCFormItem = {
  borderTop?: boolean;
  borderBottom?: boolean;
};

const CFormItem = styled(FormItem)<TCFormItem>`
  ${({ borderTop, theme }) =>
    borderTop &&
    css`
      border-top: 1px solid ${theme.gray3};
    `};

  ${({ borderBottom, theme }) =>
    borderBottom &&
    css`
      border-bottom: 1px solid ${theme.gray3};
    `};
`;

function InputField({
  componentArray,
  label,
  labelCol,
  description,
  required,
  wrapperCol,
  style,
  topDescription,
  formFieldsError,
  validation,
  borderTop = false,
  borderBottom,
}: TInputField) {
  const { formError, checkFormError } = useCheckFormError({ formFieldsError, validation });

  const getComponent = ({ componentType, idx }: TGetComponent) => {
    const component = componentArray[idx] || [];
    const {
      dataIndex = '',
      isDivider,
      dividerHeight,
      customDivider,
      customRender,
      options = [],
      formData = {},
      onChange,
      onRealtimeChange,
      onBlur,
      ...props
    } = component;
    const key = _.isArray(dataIndex) ? dataIndex.join('.') : dataIndex.toString();
    switch (componentType) {
      case 'select':
        return (
          <React.Fragment key={key}>
            <FormItem name={dataIndex} noStyle>
              <Select
                {...(props as TSelect)}
                onChange={(e: any) => {
                  onChange?.(e);
                  checkFormError(dataIndex, e, formData);
                }}
              >
                {options.map((item: any) => (
                  <SelectOption key={item.value} value={item.value}>
                    {item.iconColor ? <IconText icon={item.iconColor} message={item.label} /> : item.label}
                  </SelectOption>
                ))}
              </Select>
            </FormItem>
          </React.Fragment>
        );
      case 'radio':
        return (
          <React.Fragment key={key}>
            <FormItem name={dataIndex} noStyle>
              <RadioGroup
                {...(props as IRadioGroupParams)}
                onChange={(e: any) => {
                  onChange?.(e.target.value);
                  checkFormError(dataIndex, e.target.value, formData);
                }}
              >
                {options.map((item: any) => (
                  <Radio key={item.value} value={item.value}>
                    {item.label}
                  </Radio>
                ))}
              </RadioGroup>
            </FormItem>
          </React.Fragment>
        );

      case 'datePicker':
        return (
          <React.Fragment key={key}>
            <FormItem name={dataIndex} noStyle>
              <DatePicker
                {...(props as TDatePickerParams)}
                onChange={(e: any) => {
                  onChange?.(e);
                  checkFormError(dataIndex, e, formData);
                }}
              />
            </FormItem>
          </React.Fragment>
        );
      case 'timePicker':
        return (
          <React.Fragment key={key}>
            <FormItem name={dataIndex} noStyle>
              <TimePicker
                {...(props as ITimePickerParams)}
                format="HH:mm"
                onChange={(e: any) => {
                  onChange?.(e);
                  checkFormError(dataIndex, e, formData);
                }}
              />
            </FormItem>
          </React.Fragment>
        );
      case 'rangePicker':
        return (
          <React.Fragment key={key}>
            <FormItem name={dataIndex} noStyle>
              <DateRangePicker
                {...(props as TDateRangePickerParams)}
                onChange={(e: any) => {
                  onChange?.(e);
                  checkFormError(dataIndex, e, formData);
                }}
              />
            </FormItem>
          </React.Fragment>
        );
      case 'checkbox':
        return (
          <React.Fragment key={key}>
            <FormItem name={dataIndex} noStyle>
              <CheckboxGroup
                {...(props as ICheckboxGroupParams)}
                onChange={(e: any) => {
                  onChange?.(e);
                  checkFormError(dataIndex, e, formData);
                }}
              >
                <Row>
                  {options.map((item: any) => (
                    <Col key={item.value} span={props?.colSpan}>
                      <Checkbox value={item.value}>{item.label}</Checkbox>
                    </Col>
                  ))}
                </Row>
              </CheckboxGroup>
            </FormItem>
          </React.Fragment>
        );
      case 'checkboxWithoutAllCheck':
        return (
          <React.Fragment key={key}>
            <Col>
              <FormItem name={dataIndex} noStyle>
                <CheckboxGroup
                  {...(props as ICheckboxGroupParams)}
                  onChange={(e: any) => {
                    onChange?.(e);
                    checkFormError(dataIndex, e, formData);
                  }}
                >
                  <Row>
                    {options.map((item: { label: string; value: string | number }) => (
                      <Col key={item.value} span={props?.colSpan}>
                        <Checkbox value={item.value}>{item.label}</Checkbox>
                      </Col>
                    ))}
                  </Row>
                </CheckboxGroup>
              </FormItem>
            </Col>
          </React.Fragment>
        );
      case 'checkboxWithAllCheck':
        return (
          <React.Fragment key={key}>
            <Col>
              <FormItem name={dataIndex} noStyle>
                <CheckboxGroup
                  {...(props as ICheckboxGroupParams)}
                  onChange={(e: any) => {
                    onChange?.(e);
                    checkFormError(dataIndex, e, formData);
                  }}
                >
                  <Row>
                    {options.map((item: any) => (
                      <Col key={item.value} span={props?.colSpan} style={{ marginTop: '9px' }}>
                        <Checkbox value={item.value}>{item.label}</Checkbox>
                      </Col>
                    ))}
                  </Row>
                </CheckboxGroup>
              </FormItem>
            </Col>
            <Col>
              <CRowDivider />
            </Col>
            <Col style={{ marginTop: '9px' }}>
              <FormItem
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  (dataIndex &&
                    prevValues[dataIndex as string | number] !==
                      currentValues[dataIndex as string | number]) as boolean
                }
              >
                {({ getFieldValue, setFieldsValue }) => {
                  const currentValue = getFieldValue(dataIndex);
                  return (
                    <Checkbox
                      checked={options.length === currentValue?.length}
                      disabled={props?.disabled}
                      indeterminate={options.length > currentValue?.length && currentValue?.length > 0}
                      onChange={() => {
                        let vals: any = [];
                        if (currentValue?.length !== options.length) {
                          vals = options.map((item: any) => item.value);
                        }
                        setFieldsValue(setNestedObjects(dataIndex, vals, formData));
                        onChange?.(vals);
                        checkFormError(dataIndex, vals, formData);
                      }}
                    >
                      All
                    </Checkbox>
                  );
                }}
              </FormItem>
            </Col>
          </React.Fragment>
        );
      case 'checkboxWithCustomRender':
        return (
          <React.Fragment key={key}>
            <FormItem name={dataIndex} noStyle>
              <CheckboxGroup
                {...(props as ICheckboxGroupParams)}
                onChange={(e: any) => {
                  onChange?.(e);
                  checkFormError(dataIndex, e, formData);
                }}
              >
                {customRender?.()}
              </CheckboxGroup>
            </FormItem>
          </React.Fragment>
        );
      case 'customRender':
        return (
          <React.Fragment key={key}>
            <FormItem name={dataIndex} noStyle>
              {customRender?.()}
            </FormItem>
          </React.Fragment>
        );
      case 'button':
        return (
          <React.Fragment key={key}>
            <FormItem noStyle>
              <Button {...(props as IButtonParams)} />
            </FormItem>
            {customDivider?.()}
            {isDivider && <CRowDivider dividerHeight={dividerHeight} />}
          </React.Fragment>
        );
      case 'description':
        return (
          <React.Fragment key={key}>
            <Row style={{ ...props?.style, lineHeight: '35px' }}>{props?.text}</Row>
          </React.Fragment>
        );
      case 'textArea':
        return (
          <React.Fragment key={key}>
            <FormItem name={dataIndex} noStyle>
              <TextArea
                {...(props as TTextAreaParams)}
                name={dataIndex as string}
                onBlur={(e: any) => {
                  onChange?.(e.target.value);
                  onBlur?.(e.target.value);
                }}
                onChange={(e: any) => {
                  // 연속입력가능한 input은 onChange를 focusOut(onBlur)시에 실행하고(re-rendering 성능 issue)
                  // 실제 onChange는 onRealtimeChange로 분리(사용가능성)
                  onRealtimeChange?.(e.target.value);
                  checkFormError(dataIndex, e.target.value, formData);
                }}
              />
            </FormItem>
          </React.Fragment>
        );
      case 'password':
        return (
          <React.Fragment key={key}>
            <FormItem name={dataIndex} noStyle>
              <Password
                {...(props as TPasswordParams)}
                onBlur={(e: any) => {
                  onChange?.(e.target.value);
                  onBlur?.(e.target.value);
                }}
                onChange={(e: any) => {
                  // 연속입력가능한 input은 onChange를 focusOut(onBlur)시에 실행하고(re-rendering 성능 issue)
                  // 실제 onChange는 onRealtimeChange로 분리(사용가능성)
                  onRealtimeChange?.(e.target.value);
                  checkFormError(dataIndex, e.target.value, formData);
                }}
              />
            </FormItem>
          </React.Fragment>
        );
      case 'number':
        return (
          <React.Fragment key={key}>
            <FormItem name={dataIndex} noStyle>
              <InputNumber
                {...(props as TInputNumberParams)}
                onBlur={(e: any) => {
                  onChange?.(e);
                  onBlur?.(e);
                }}
                onChange={(e: any) => {
                  // 연속입력가능한 input은 onChange를 focusOut(onBlur)시에 실행하고(re-rendering 성능 issue)
                  // 실제 onChange는 onRealtimeChange로 분리(사용가능성)
                  onRealtimeChange?.(e);
                  checkFormError(dataIndex, e, formData);
                }}
              />
            </FormItem>
          </React.Fragment>
        );
      case 'text':
        return (
          <React.Fragment key={key}>
            <FormItem name={dataIndex} noStyle>
              <Input
                {...(props as TInputParams)}
                name={dataIndex as string}
                onBlur={(e: any) => {
                  onChange?.(e.target.value);
                  onBlur?.(e.target.value);
                }}
                onChange={(e: any) => {
                  // 연속입력가능한 input은 onChange를 focusOut(onBlur)시에 실행하고(re-rendering 성능 issue)
                  // 실제 onChange는 onRealtimeChange로 분리(사용가능성)
                  onRealtimeChange?.(e.target.value);
                  checkFormError(dataIndex, e.target.value, formData);
                }}
              />
            </FormItem>
          </React.Fragment>
        );
      default:
        return null;
    }
  };

  return (
    <CFormItem
      borderBottom={borderBottom}
      borderTop={borderTop}
      label={label}
      labelCol={{ span: labelCol ?? 4 }}
      required={required}
      style={style}
      wrapperCol={{ span: wrapperCol || (label ? 20 : 24) }}
    >
      {topDescription && topDescription()}
      <InputGroup compact>
        {componentArray?.map((item: any, idx: any) => {
          if (item?.gone) {
            const component = componentArray[idx];
            const key = _.isArray(component.dataIndex) ? component.dataIndex.join('_') : component.dataIndex;
            return <React.Fragment key={key} />;
          }
          return getComponent({ componentType: item?.type, idx });
        })}
      </InputGroup>
      {componentArray?.map((item: any) => {
        const dataIndex = item?.dataIndex || '';
        return <FormValidErrorMessage formError={getNestedObjects(dataIndex, formError)} key={dataIndex} />;
      })}
      {description && <Row style={{ marginTop: '5px' }}>{description}</Row>}
    </CFormItem>
  );
}

export default InputField;
